import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapPin, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import ProfileField from '../ProfileField';

function ProfileDashboard() {
  const [isEditing, setIsEditing] = useState(false);
  const [playerData, setPlayerData] = useState({});
  const [editData, setEditData] = useState({});
  const [newAchievement, setNewAchievement] = useState({ event: '', medal: '', year: '', link: '' });
  const navigate = useNavigate();
  const {logout, userData} = useAuth()

  useEffect(() => {
    async function fetchPlayer() {
      try {
        const res = await axios.get(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/player/${userData.playerId}`, {
          withCredentials:true
        });
        setPlayerData(res.data);
        setEditData(res.data);
      } catch (err) {
        console.error('Failed to fetch player:', err);
      }
    }
    fetchPlayer();
  }, [userData.playerId]);

  const startEdit = () => {
    setIsEditing(true);
    console.log('Starting edit with player data:', playerData);
    console.log('Player achievements:', playerData.achievements);
    setEditData(playerData);
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditData(playerData);
  };

  // PUT request to update player data
  async function updatePlayer(updatedData) {
    console.log(updatedData)
    try {
      const res = await axios.put(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/player/${userData.playerId}`, updatedData, {
        withCredentials: true
      });
      return res.data;
    } catch (err) {
      console.error('Failed to update player:', err);
      throw err;
    }
  }

  const saveEdit = async () => {
    try {
      console.log('Saving edit data:', editData);
      const updated = await updatePlayer(editData);
      setPlayerData(updated);
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving edit:', err);
      // Optionally show error to user
    }
  };

  const handleFieldChange = useCallback((field, value) => {
    setEditData(prevData => {
      if (field.includes('.')) {
        const [parent, child] = field.split('.');
        return {
          ...prevData,
          [parent]: {
            ...prevData[parent],
            [child]: value,
          },
        };
      } else {
        return { ...prevData, [field]: value };
      }
    });
  }, []);

  const deleteAchievement = useCallback((index) => {
    setEditData(prevData => {
      const currentAchievements = prevData.achievements || [];
      const updatedAchievements = currentAchievements.filter((_, i) => i !== index);
      return { ...prevData, achievements: updatedAchievements };
    });
  }, []);

  const handleNewAchievementChange = useCallback((e) => {
    const { name, value } = e.target;
    setNewAchievement(prev => ({ ...prev, [name]: value }));
  }, []);

  const addAchievement = useCallback(() => {
    if (!newAchievement.event || !newAchievement.medal || !newAchievement.year) return;
    const newAch = { ...newAchievement, _id: Date.now().toString() };
    console.log('Adding achievement:', newAch);
    setEditData(prevData => {
      const currentAchievements = prevData.achievements || [];
      console.log('Current achievements:', currentAchievements);
      return { ...prevData, achievements: [...currentAchievements, newAch] };
    });
    setNewAchievement({ event: '', medal: '', year: '', link: '' });
  }, [newAchievement]);

  const getMedalColor = useCallback((medal) => {
    const colors = {
      Gold: 'text-yellow-600',
      Silver: 'text-gray-500',
      Bronze: 'text-amber-600',
    };
    return colors[medal] || 'text-gray-600';
  }, []);

  const getMedalIcon = useCallback((medal) => {
    const icons = {
      Gold: '🥇',
      Silver: '🥈',
      Bronze: '🥉',
    };
    return icons[medal] || '🏅';
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${process.env.REACT_APP_MINF_LATEST_BAC_BASE}/api/player/logout`, {}, {
        withCredentials: true // ✅ Important: needed if cookie is HTTP-only
      });
      logout()
      navigate('/login'); // ✅ Redirect to login after logout
    } catch (error) {
      console.error("Logout failed", error);
      // Optional: Add toast or alert
    }
  }, [logout, navigate]);



  // Add helper for nested value access
  const getNestedValue = useCallback((obj, path) => {
    return path.split('.').reduce((o, p) => (o ? o[p] : ''), obj);
  }, []);

  // Memoize achievements list
  const achievementsList = useMemo(() => {
    const achievements = (isEditing ? editData.achievements : playerData.achievements) || [];
    return achievements;
  }, [isEditing, editData.achievements, playerData.achievements]);

  // Memoize date formatting
  const formattedDob = useMemo(() => {
    return playerData.dob ? new Date(playerData.dob).toLocaleDateString() : '';
  }, [playerData.dob]);

  // Memoize address formatting
  const formattedAddress = useMemo(() => {
    return `${playerData.address}, ${playerData.district}`;
  }, [playerData.address, playerData.district]);

  return (
    <div className="min-h-screen mt-12 ">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Status Check */}
        {playerData.status === "pending" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  ID Not Activated Yet
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Your player ID is currently pending approval. You will be able to access all features once your account is activated by the administrator.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Main Profile Card */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
          {/* Cover Photo */}
          <div className="relative h-48 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-500">
            <div className="absolute inset-0" style={{
              backgroundImage: 'url("https://i.pinimg.com/736x/96/5c/8f/965c8f6a1faedc80d75e670288001547.jpg")',
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              opacity: 0.8
            }}></div>
            <button className="absolute top-4 flex items-center gap-2 right-4 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-white/30 transition-colors" onClick={startEdit}
            >
              Edit<Edit size={14} />
            </button>
            <button className="absolute top-4 right-28 flex items-center gap-2 bg-red-500/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors z-10" onClick={handleLogout}>
              Logout
            </button>
          </div>

          {/* Profile Info */}
          <div className="relative px-6 pb-6">
            {/* Avatar */}
            <div className="flex items-end -mt-16 mb-4">
              <div className="relative">
                <img
                  src={playerData.photo}
                  alt={playerData.name}
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover"
                />
              </div>
            </div>

            {/* Name and Basic Info */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">{playerData.name}</h2>
              <div className="flex items-center text-gray-600 text-sm mb-2">
                <span>{playerData.sport} Fighter</span>
                <span className="mx-2">•</span>
                <MapPin size={14} className="mr-1" />
                <span>{playerData.district}, {playerData.state}</span>
              </div>
              <div className="flex gap-2 mt-3">
                <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                  {playerData.playerId}
                </span>
                <span className="bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                  {playerData.rank}
                </span>
                <span className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-xs font-medium">
                  {playerData.weightCategory} • {playerData.ageCategory}
                </span>
                {playerData.status && (
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border ${
                    playerData.status === "approved" || playerData.status === "success"
                      ? "bg-green-100 text-green-700 border-green-200"
                      : playerData.status === "pending"
                        ? "bg-yellow-100 text-yellow-700 border-yellow-200"
                        : "bg-red-100 text-red-700 border-red-200"
                  }`}>
                    {playerData.status}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Information Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
            <div className="space-y-0">
              <ProfileField 
                label="Name" 
                value={isEditing ? editData.name : playerData.name} 
                fieldKey="name" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Player ID" 
                value={isEditing ? editData.playerId : playerData.playerId} 
                fieldKey="playerId" 
                showEdit={false}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Email" 
                value={isEditing ? editData.email : playerData.email} 
                fieldKey="email" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Mobile" 
                value={isEditing ? editData.mobile : playerData.mobile} 
                fieldKey="mobile" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Date of Birth" 
                value={isEditing ? editData.dob : formattedDob} 
                fieldKey="dob" 
                showEdit={true} 
                type="date"
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Gender" 
                value={isEditing ? editData.gender : playerData.gender} 
                fieldKey="gender" 
                showEdit={true} 
                type="select"
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Blood Group" 
                value={isEditing ? editData.bloodGroup : playerData.bloodGroup} 
                fieldKey="bloodGroup" 
                showEdit={true} 
                type="select"
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Nationality" 
                value={isEditing ? editData.nationality : playerData.nationality} 
                fieldKey="nationality" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
            </div>
          </div>

          {/* Physical Stats */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Physical Stats</h3>
            <div className="space-y-0">
              <ProfileField 
                label="Height" 
                value={isEditing ? editData.height : `${playerData.height} cm`} 
                fieldKey="height" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Weight" 
                value={isEditing ? editData.weight : `${playerData.weight} kg`} 
                fieldKey="weight" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Weight Category" 
                value={isEditing ? editData.weightCategory : playerData.weightCategory} 
                fieldKey="weightCategory" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-0">
              <ProfileField 
                label="Address" 
                value={isEditing ? editData.address : formattedAddress} 
                fieldKey="address" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="State" 
                value={isEditing ? editData.state : playerData.state} 
                fieldKey="state" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Pincode" 
                value={isEditing ? editData.pincode : playerData.pincode} 
                fieldKey="pincode" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Emergency Contact Name" 
                value={isEditing ? editData.emergencyContact?.name : playerData.emergencyContact?.name} 
                fieldKey="emergencyContact.name" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField 
                label="Emergency Phone" 
                value={isEditing ? editData.emergencyContact?.phone : playerData.emergencyContact?.phone} 
                fieldKey="emergencyContact.phone" 
                showEdit={true}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
            </div>
          </div>

          {/* Social Links */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Social Links</h3>
            <div className="space-y-0">
              <ProfileField
                label="Instagram"
                value={isEditing ? editData.socialLinks?.instagram : playerData.socialLinks?.instagram}
                fieldKey="socialLinks.instagram"
                showEdit={true}
                link={!isEditing && playerData.socialLinks?.instagram ? playerData.socialLinks.instagram : null}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField
                label="Facebook"
                value={isEditing ? editData.socialLinks?.facebook : playerData.socialLinks?.facebook}
                fieldKey="socialLinks.facebook"
                showEdit={true}
                link={!isEditing && playerData.socialLinks?.facebook ? playerData.socialLinks.facebook : null}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
              <ProfileField
                label="Twitter"
                value={isEditing ? editData.socialLinks?.twitter : playerData.socialLinks?.twitter}
                fieldKey="socialLinks.twitter"
                showEdit={true}
                link={!isEditing && playerData.socialLinks?.twitter ? playerData.socialLinks.twitter : null}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
            </div>
          </div>
        </div>

        {/* Achievements Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Achievements</h3>
          <div className="space-y-4">
            {achievementsList.map((achievement, index) => (
              <div
                key={achievement._id}
                className="group flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-200"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center text-xl">
                    {getMedalIcon(achievement.medal)}
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">{achievement.event}</h4>
                    <p className="text-sm text-gray-500">{achievement.year}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${getMedalColor(achievement.medal)}`}>{achievement.medal}</span>
                  {isEditing && <button
                    className="ml-2 text-red-500 hover:text-red-700 text-xs font-medium"
                    onClick={() => deleteAchievement(index)}
                  >
                    <Trash2 size={15}/>
                  </button>}
                </div>
              </div>
            ))}
          </div>
          {/* Add Achievement Form */}
          {isEditing &&
            <div className="mt-6 border-t pt-4">
              <h4 className="font-semibold text-gray-800 mb-2 text-sm">Add Achievement</h4>
              <div className="flex flex-col md:flex-row gap-2 mb-2">
                <input
                  type="text"
                  name="event"
                  placeholder="Event"
                  value={newAchievement.event}
                  onChange={handleNewAchievementChange}
                  className="border rounded px-2 py-1 text-sm"
                />
                <select
                  name="medal"
                  value={newAchievement.medal}
                  onChange={handleNewAchievementChange}
                  className="border rounded px-2 py-1 text-sm"
                >
                  <option value="">Medal</option>
                  <option value="Gold">Gold</option>
                  <option value="Silver">Silver</option>
                  <option value="Bronze">Bronze</option>
                </select>
                <input
                  type="text"
                  name="year"
                  placeholder="Year"
                  value={newAchievement.year}
                  onChange={handleNewAchievementChange}
                  className="border rounded px-2 py-1 text-sm"
                />
                <input
                  type="text"
                  name="link"
                  placeholder="Certificate Link (optional)"
                  value={newAchievement.link}
                  onChange={handleNewAchievementChange}
                  className="border rounded px-2 py-1 text-sm"
                />
                <button
                  className="bg-green-500 text-white px-4 py-1 rounded text-sm font-medium hover:bg-green-600"
                  onClick={addAchievement}
                  type="button"
                >
                  Add
                </button>
              </div>
            </div>
          }
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Documents</h3>
                      <div className="space-y-0">
              <ProfileField
                label="ID Proof"
                value={playerData.idProof ? "View Document" : "No document uploaded"}
                link={playerData.idProof}
                showEdit={false}
                isEditing={isEditing}
                editData={editData}
                handleFieldChange={handleFieldChange}
                getNestedValue={getNestedValue}
              />
            </div>
        </div>

        {/* Floating Save/Cancel Buttons */}
        {isEditing && (
          <div className="sticky mt-12 rounded-xl bottom-3 left-0 w-full flex justify-center bg-black/40 backdrop-blur-sm py-4 shadow-lg z-50">
            <button
              className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg mr-4 font-medium hover:bg-gray-300"
              onClick={cancelEdit}
            >
              Cancel
            </button>
            <button
              className="bg-gray-950 border-2 border-gray-950 text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-200 hover:text-gray-950"
              onClick={saveEdit}
            >
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProfileDashboard;
